import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Award, Repeat, Lock, Star, ArrowRight, Palmtree, ShieldCheck } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { SearchBar } from "@/components/SearchBar";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { useFlags } from "@/hooks/useFlags";
import { islandsFor } from "@/lib/islands";

const HERO = "https://images.unsplash.com/photo-1621695644555-5fe0f2f87939?w=1920&q=80";


export default function Home() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const v2 = useFlags().v2_enabled;
  const v3 = useFlags().v3_enabled;
  const [vehicles, setVehicles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!topRated.length) return;
    const id = setInterval(() => {
      const el = carouselRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      el.scrollTo({ left: el.scrollLeft + 272 >= max ? 0 : el.scrollLeft + 272, behavior: "smooth" });
    }, 3500);
    return () => clearInterval(id);
  }, [topRated]);

  useEffect(() => {
    api.get("/vehicles?featured=true").then((r) => setVehicles(r.data.vehicles)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.testimonials)).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data.partners)).catch(() => {});
  }, []);

  useEffect(() => {
    if (v3) {
      api.get("/activities/featured").then((r) => setFeatured(r.data.activities)).catch(() => {});
      api.get("/activities", { params: { sort: "rating" } }).then((r) => setTopRated((r.data.activities || []).filter((a) => a.rating > 0).slice(0, 8))).catch(() => {});
    }
  }, [v3]);

  const why = [
    { icon: Award, title: t("why.quality"), text: t("why.qualityText") },
    { icon: Repeat, title: t("why.flex"), text: t("why.flexText") },
    { icon: Lock, title: t("why.security"), text: t("why.securityText") },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[620px] flex items-center">
        <img src={HERO} alt="Caribbean" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 w-full">
          <div className="max-w-2xl text-white animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-4 py-1.5 rounded-full text-sm font-semibold mb-5">
              <Palmtree className="w-4 h-4 text-emeraude" /> Martinique · Guadeloupe · Saint-Martin · Sint Maarten
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-pastel font-body leading-relaxed max-w-xl">{t("hero.subtitle")}</p>
          </div>
          <div className="mt-8 max-w-5xl animate-fade-up" style={{ animationDelay: "150ms" }}>
            <SearchBar />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-xs font-bold tracking-wider uppercase text-emeraude">Caribbean Drive</p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mt-2">{t("why.title")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {why.map((w, i) => (
            <div key={w.title} data-testid={`why-card-${i}`} className="bg-white rounded-2xl p-8 shadow-card border border-border hover:-translate-y-1 transition-transform animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="w-14 h-14 rounded-2xl bg-canard/10 flex items-center justify-center mb-5">
                <w.icon className="w-7 h-7 text-canard" />
              </div>
              <h3 className="font-heading font-bold text-xl text-ink">{w.title}</h3>
              <p className="text-muted2 font-body mt-2 leading-relaxed">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="section-sand py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-bold tracking-wider uppercase text-emeraude">{t("featured.subtitle")}</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mt-2">{t("featured.title")}</h2>
            </div>
            <Button variant="ghost" data-testid="view-all-vehicles-button" onClick={() => nav("/search")} className="hidden sm:flex rounded-full font-bold text-canard">
              {t("featured.viewAll")} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {vehicles.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
          </div>
          <div className="text-center mt-10 sm:hidden">
            <Button onClick={() => nav("/search")} className="rounded-full bg-canard">{t("featured.viewAll")}</Button>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink">{t("destinations.title")}</h2>
          <p className="text-muted2 font-body mt-2">{t("destinations.subtitle")}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {islandsFor(v3).map((d, i) => (
            <button key={d.v} data-testid={`destination-${d.v}`} onClick={() => nav(`/search?country=${d.v}`)}
              className="group relative h-64 rounded-2xl overflow-hidden shadow-card animate-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
              <img src={d.img} alt={d.l} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-canard-dark/90 via-canard-dark/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5 text-left">
                <h3 className="text-white font-heading font-bold text-xl">{d.l}</h3>
                <span className="text-pastel text-sm font-semibold flex items-center gap-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Explorer <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {v3 && topRated.length > 0 && (
        <section className="section-sand py-16" data-testid="home-top-rated">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-6">⭐ {t("activities.topRated")}</h2>
            <div className="flex gap-5 overflow-x-auto pb-4 -mx-1 px-1" ref={carouselRef}>
              {topRated.map((a) => (
                <button key={a.id} data-testid={`top-rated-${a.id}`} onClick={() => nav(`/activites/${a.id}`)} className="shrink-0 w-64 text-left bg-white rounded-2xl border border-border overflow-hidden shadow-card hover:shadow-lg transition-shadow">
                  <div className="h-40 overflow-hidden"><img src={fileUrl(a.image)} alt={a.name} className="w-full h-full object-cover" /></div>
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-heading font-bold text-ink line-clamp-1">{a.name}</p>
                      <span className="text-sm font-bold text-canard-dark shrink-0">★ {a.rating}</span>
                    </div>
                    <span className="text-sm font-bold text-canard mt-1 inline-block">{t("activities.from")} {a.price_from}€</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED ACTIVITIES (V3) */}
      {v3 && featured.length > 0 && (
        <section className="section-sand py-20" data-testid="home-featured-activities">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink">{t("homeFeatured.title")}</h2>
                <p className="text-muted2 font-body mt-2">{t("homeFeatured.sub")}</p>
              </div>
              <Button variant="outline" data-testid="home-featured-cta" onClick={() => nav("/activites")} className="rounded-full font-bold shrink-0 hidden sm:flex">{t("homeFeatured.cta")} <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.slice(0, 6).map((a, i) => (
                <button key={a.id} data-testid={`home-activity-${a.id}`} onClick={() => nav("/activites")} className="group text-left bg-white rounded-2xl border border-border overflow-hidden shadow-card animate-fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={fileUrl(a.image)} alt={a.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    {a.partner_id && <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] font-bold text-emeraude-dark bg-white/90 px-2.5 py-1 rounded-full shadow-sm"><ShieldCheck className="w-3.5 h-3.5" /> {t("activities.verified")}</span>}
                  </div>
                  <div className="p-4">
                    <p className="font-heading font-bold text-ink">{a.name}</p>
                    <p className="text-sm text-muted2 mt-1 line-clamp-2">{a.description}</p>
                    <span className="text-sm font-bold text-canard mt-3 inline-block">{t("activities.from")} {a.price_from}€</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="section-sand py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink text-center mb-12">{t("testimonials.title")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((tm, i) => (
              <div key={tm.id} data-testid={`testimonial-${i}`} className="bg-white rounded-2xl p-6 shadow-card border border-border">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, s) => <Star key={s} className={`w-4 h-4 ${s < tm.rating ? "fill-emeraude text-emeraude" : "text-border"}`} />)}
                </div>
                <p className="text-ink font-body text-sm leading-relaxed">"{tm.text}"</p>
                <p className="mt-4 font-heading font-bold text-canard-dark text-sm">{tm.name} · <span className="text-muted2 font-body">{tm.country}</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <p className="text-center text-xs font-bold tracking-wider uppercase text-muted2 mb-8">{t("partners.title")}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {partners.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-canard-dark/70 font-heading font-bold text-lg">
              <ShieldCheck className="w-5 h-5 text-emeraude" /> {p.name}
            </div>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      {v2 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-4" data-testid="home-join-v2">
          <div className="rounded-3xl border border-canard/20 bg-white p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 justify-between">
            <div>
              <h3 className="font-heading font-extrabold text-2xl text-ink flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-canard" /> {t("homeJoin.title")}</h3>
              <p className="text-muted2 font-body mt-2 max-w-xl">{t("homeJoin.sub")}</p>
            </div>
            <Button data-testid="home-join-cta" onClick={() => nav("/tarifs")} className="rounded-full bg-canard hover:bg-canard-dark font-bold h-12 px-7 shrink-0">{t("homeJoin.cta")} <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="relative rounded-3xl overflow-hidden bg-canard-dark p-10 sm:p-16 text-center">
          <div className="relative z-10">
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white">{t("hero.cta")}</h2>
            <p className="text-pastel font-body mt-3 max-w-lg mx-auto">{t("hero.subtitle")}</p>
            <Button data-testid="cta-banner-button" onClick={() => nav("/search")} className="mt-7 h-13 px-8 py-3 rounded-full bg-emeraude hover:bg-emeraude-dark text-white font-heading font-bold text-base shadow-cta">
              {t("hero.cta")} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <Palmtree className="absolute -right-6 -bottom-6 w-48 h-48 text-emeraude/20" />
        </div>
      </section>
    </div>
  );
}
